import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import {
    FiMaximize2,
    FiMapPin,
    FiCheckCircle,
    FiPlay,
    FiImage,
    FiVideo,
    FiAlertCircle,
    FiRefreshCw,
} from 'react-icons/fi';
import { useLightbox } from '../../context/LightboxContext';
import onGroundMediaService from '../../services/onGroundMediaService';
import { useRealtimeSync } from '../../hooks/useRealtimeSync';
import VideoModal from './VideoModal';
import './ShopGallerySection.css';

const ShopGallerySection = () => {
    const { openLightbox } = useLightbox();
    const [mediaItems, setMediaItems] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState('');
    const [activeFilter, setActiveFilter] = useState('all');
    const [activeVideo, setActiveVideo] = useState(null);
    const [failedMediaIds, setFailedMediaIds] = useState({});
    const [failedThumbIds, setFailedThumbIds] = useState({});

    const isMountedRef = useRef(true);
    const latestFetchIdRef = useRef(0);

    useEffect(() => {
        isMountedRef.current = true;
        return () => {
            isMountedRef.current = false;
        };
    }, []);

    const fetchMedia = useCallback(async ({ showLoading = true } = {}) => {
        const fetchId = ++latestFetchIdRef.current;

        try {
            if (showLoading) {
                setLoading(true);
            }
            setError('');

            const items = await onGroundMediaService.getActiveMedia({
                active_only: true,
            });

            if (!isMountedRef.current || fetchId !== latestFetchIdRef.current) {
                return;
            }

            const normalizedList = Array.isArray(items) ? items : [];
            setMediaItems(normalizedList);
            setFailedMediaIds({});
            setFailedThumbIds({});

            // Reconcile currently open video modal if updated, unpublished, or deleted
            setActiveVideo((prevVideo) => {
                if (!prevVideo) return null;
                const updatedItem = normalizedList.find(
                    (entry) =>
                        entry.mediaType === 'video' &&
                        (entry.id === prevVideo.id || entry._id === prevVideo._id)
                );
                return updatedItem ? updatedItem.videoModalData : null;
            });
        } catch (err) {
            if (!isMountedRef.current || fetchId !== latestFetchIdRef.current) {
                return;
            }

            if (showLoading) {
                setMediaItems([]);
                setError(
                    err?.message ||
                        'Unable to load showroom media right now. Please try again.'
                );
            }
        } finally {
            if (
                isMountedRef.current &&
                fetchId === latestFetchIdRef.current &&
                showLoading
            ) {
                setLoading(false);
            }
        }
    }, []);

    useEffect(() => {
        fetchMedia({ showLoading: true });
    }, [fetchMedia]);

    // Silently refresh when user switches back to the browser tab
    useEffect(() => {
        if (typeof document === 'undefined') return undefined;

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                fetchMedia({ showLoading: false });
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () =>
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            );
    }, [fetchMedia]);

    // Real-time SSE sync when admin adds, edits, reorders, publishes, unpublishes, or deletes media
    const handleRealtimeMediaUpdate = useCallback(() => {
        fetchMedia({ showLoading: false });
    }, [fetchMedia]);

    useRealtimeSync('on_ground_media', handleRealtimeMediaUpdate);
    useRealtimeSync('on-ground-media', handleRealtimeMediaUpdate);
    useRealtimeSync('ongroundmedia', handleRealtimeMediaUpdate);
    useRealtimeSync('shop_gallery', handleRealtimeMediaUpdate);
    useRealtimeSync('shop-gallery', handleRealtimeMediaUpdate);
    useRealtimeSync('gallery', handleRealtimeMediaUpdate);

    // Build dynamic filter tabs from the categories present in published media items
    const filterCategories = useMemo(() => {
        if (!Array.isArray(mediaItems) || mediaItems.length === 0) {
            return [];
        }

        const seen = new Map();
        mediaItems.forEach((item) => {
            if (item.category && !seen.has(item.category)) {
                seen.set(
                    item.category,
                    item.categoryTabLabel || item.categoryLabel || 'Showroom'
                );
            }
        });

        const dynamicTabs = Array.from(seen.entries()).map(([key, label]) => ({
            key,
            label,
        }));

        return [
            { key: 'all', label: 'All Showroom Displays' },
            ...dynamicTabs,
        ];
    }, [mediaItems]);

    // Reset activeFilter to 'all' if selected category has no items after an update
    useEffect(() => {
        if (activeFilter === 'all') return;
        const stillExists = filterCategories.some(
            (cat) => cat.key === activeFilter
        );
        if (!stillExists) {
            setActiveFilter('all');
        }
    }, [activeFilter, filterCategories]);

    const filteredItems = useMemo(() => {
        if (activeFilter === 'all') return mediaItems;
        return mediaItems.filter((item) => item.category === activeFilter);
    }, [activeFilter, mediaItems]);

    const handleCardOpen = useCallback(
        (item) => {
            if (!item) return;

            if (item.mediaType === 'video' && item.videoModalData) {
                setActiveVideo(item.videoModalData);
                return;
            }

            if (item.imageUrl && !failedMediaIds[item.id]) {
                openLightbox(item.imageUrl, item.title, item.desc);
            }
        },
        [failedMediaIds, openLightbox]
    );

    const markMediaFailed = useCallback((id) => {
        setFailedMediaIds((prev) => ({ ...prev, [id]: true }));
    }, []);

    const markThumbFailed = useCallback((id) => {
        setFailedThumbIds((prev) => ({ ...prev, [id]: true }));
    }, []);

    return (
        <section
            id="shop-gallery-section"
            className="shop-gallery-section"
            aria-label="Real Shop Gallery"
        >
            <div className="container">
                {/* Header */}
                <div className="shop-gallery-header">
                    <span className="section-eyebrow">AUTHENTIC ON-GROUND MEDIA</span>
                    <h2 className="section-title">Explore Our Lahore Showroom</h2>
                    <p className="shop-gallery-subtitle">
                        See authentic photos of our actual shop environment, brand displays, and genuine box-packed stock at Main Bazar Chandni Chowk, Lahore.
                    </p>
                    <div className="shop-gallery-accent-line" aria-hidden="true" />
                </div>

                {loading && mediaItems.length === 0 ? (
                    <div
                        className="shop-gallery-grid"
                        aria-label="Loading showroom media"
                    >
                        {[0, 1, 2, 3].map((idx) => (
                            <div
                                key={`gallery-skeleton-${idx}`}
                                className="shop-gallery-card shop-gallery-card--skeleton"
                                aria-hidden="true"
                            >
                                <div className="shop-gallery-img-wrap shop-gallery-skeleton-shimmer" />
                                <div className="shop-gallery-card-info">
                                    <div className="shop-gallery-skeleton-line shop-gallery-skeleton-line--tag shop-gallery-skeleton-shimmer" />
                                    <div className="shop-gallery-skeleton-line shop-gallery-skeleton-line--title shop-gallery-skeleton-shimmer" />
                                    <div className="shop-gallery-skeleton-line shop-gallery-skeleton-line--desc shop-gallery-skeleton-shimmer" />
                                </div>
                            </div>
                        ))}
                    </div>
                ) : error && mediaItems.length === 0 ? (
                    <div className="shop-gallery-state-box shop-gallery-state-box--error" role="alert">
                        <FiAlertCircle className="shop-gallery-state-icon" aria-hidden="true" />
                        <p className="shop-gallery-state-text">{error}</p>
                        <button
                            type="button"
                            className="shop-gallery-retry-btn"
                            onClick={() => fetchMedia({ showLoading: true })}
                        >
                            <FiRefreshCw aria-hidden="true" />
                            <span>Retry</span>
                        </button>
                    </div>
                ) : mediaItems.length === 0 ? (
                    <div className="shop-gallery-state-box">
                        <FiImage className="shop-gallery-state-icon" aria-hidden="true" />
                        <p className="shop-gallery-state-text">
                            No showroom media has been published yet.
                        </p>
                    </div>
                ) : (
                    <>
                        {/* Filter Tabs */}
                        {filterCategories.length > 1 && (
                            <div
                                className="shop-gallery-tabs"
                                role="tablist"
                                aria-label="Showroom gallery filters"
                            >
                                {filterCategories.map((cat) => (
                                    <button
                                        key={cat.key}
                                        type="button"
                                        role="tab"
                                        aria-selected={activeFilter === cat.key}
                                        className={`shop-gallery-tab-btn ${
                                            activeFilter === cat.key ? 'is-active' : ''
                                        }`}
                                        onClick={() => setActiveFilter(cat.key)}
                                    >
                                        {cat.label}
                                    </button>
                                ))}
                            </div>
                        )}

                        {/* Media Grid */}
                        {filteredItems.length === 0 ? (
                            <div className="shop-gallery-state-box">
                                <FiImage
                                    className="shop-gallery-state-icon"
                                    aria-hidden="true"
                                />
                                <p className="shop-gallery-state-text">
                                    No media items found in this category.
                                </p>
                                <button
                                    type="button"
                                    className="shop-gallery-retry-btn"
                                    onClick={() => setActiveFilter('all')}
                                >
                                    <span>View All Showroom Displays</span>
                                </button>
                            </div>
                        ) : (
                            <div className="shop-gallery-grid">
                                {filteredItems.map((item) => {
                                    const isVideo = item.mediaType === 'video';
                                    const isMediaBroken = Boolean(failedMediaIds[item.id]);
                                    const hasVideoThumb =
                                        isVideo &&
                                        Boolean(item.thumbnailUrl) &&
                                        !failedThumbIds[item.id];
                                    const canPreviewDirectVideo =
                                        isVideo &&
                                        !hasVideoThumb &&
                                        item.isDirectVideo &&
                                        Boolean(item.videoSrc) &&
                                        !isMediaBroken;
                                    const previewVideoUrl =
                                        canPreviewDirectVideo &&
                                        !item.videoSrc.includes('#')
                                            ? `${item.videoSrc}#t=0.1`
                                            : item.videoSrc;

                                    return (
                                        <div
                                            key={item.id}
                                            className={`shop-gallery-card ${
                                                isVideo ? 'shop-gallery-card--video' : ''
                                            }`}
                                            onClick={() => handleCardOpen(item)}
                                            role="button"
                                            tabIndex={0}
                                            title={
                                                isVideo
                                                    ? `Tap to play: ${item.title}`
                                                    : `Tap to zoom: ${item.title}`
                                            }
                                            onKeyDown={(e) => {
                                                if (e.key === 'Enter' || e.key === ' ') {
                                                    e.preventDefault();
                                                    handleCardOpen(item);
                                                }
                                            }}
                                        >
                                            <div className="shop-gallery-img-wrap">
                                                {isVideo ? (
                                                    hasVideoThumb ? (
                                                        <img
                                                            src={item.thumbnailUrl}
                                                            alt={item.altText}
                                                            className="shop-gallery-img"
                                                            loading="lazy"
                                                            onError={() =>
                                                                markThumbFailed(item.id)
                                                            }
                                                        />
                                                    ) : canPreviewDirectVideo ? (
                                                        <video
                                                            src={previewVideoUrl}
                                                            muted
                                                            playsInline
                                                            preload="metadata"
                                                            disablePictureInPicture
                                                            aria-hidden="true"
                                                            tabIndex={-1}
                                                            className="shop-gallery-img"
                                                            onError={() =>
                                                                markMediaFailed(item.id)
                                                            }
                                                        />
                                                    ) : (
                                                        <div className="shop-gallery-media-fallback">
                                                            <FiVideo size={30} />
                                                            <span>{item.title}</span>
                                                        </div>
                                                    )
                                                ) : !isMediaBroken ? (
                                                    <img
                                                        src={item.imageUrl}
                                                        alt={item.altText}
                                                        className="shop-gallery-img"
                                                        loading="lazy"
                                                        onError={() =>
                                                            markMediaFailed(item.id)
                                                        }
                                                    />
                                                ) : (
                                                    <div className="shop-gallery-media-fallback">
                                                        <FiImage size={30} />
                                                        <span>{item.title}</span>
                                                    </div>
                                                )}

                                                <div className="shop-gallery-overlay">
                                                    {item.highlight ? (
                                                        <div className="shop-gallery-badge">
                                                            <FiCheckCircle
                                                                className="badge-icon"
                                                                aria-hidden="true"
                                                            />
                                                            <span>{item.highlight}</span>
                                                        </div>
                                                    ) : (
                                                        <span aria-hidden="true" />
                                                    )}

                                                    {isVideo && (
                                                        <div
                                                            className="shop-gallery-play-indicator"
                                                            aria-hidden="true"
                                                        >
                                                            <FiPlay className="shop-gallery-play-icon" />
                                                        </div>
                                                    )}

                                                    <div className="shop-gallery-zoom-pill">
                                                        {isVideo ? (
                                                            <>
                                                                <FiPlay aria-hidden="true" />
                                                                <span>Play Video</span>
                                                            </>
                                                        ) : (
                                                            <>
                                                                <FiMaximize2 aria-hidden="true" />
                                                                <span>View Full Size</span>
                                                            </>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="shop-gallery-card-info">
                                                {item.categoryLabel && (
                                                    <div className="shop-gallery-meta-tag">
                                                        <FiMapPin
                                                            className="pin-icon"
                                                            aria-hidden="true"
                                                        />
                                                        <span>{item.categoryLabel}</span>
                                                    </div>
                                                )}
                                                <h3 className="shop-gallery-card-title">
                                                    {item.title}
                                                </h3>
                                                {item.desc && (
                                                    <p className="shop-gallery-card-desc">
                                                        {item.desc}
                                                    </p>
                                                )}
                                            </div>
                                        </div>
                                    );
                                })}
                            </div>
                        )}
                    </>
                )}
            </div>

            {/* Video Playback Modal for On-Ground Video Items */}
            <VideoModal
                isOpen={Boolean(activeVideo)}
                video={activeVideo}
                onClose={() => setActiveVideo(null)}
            />
        </section>
    );
};

export default ShopGallerySection;
